import React, { useState, useRef, useEffect } from 'react';
import { Bars3Icon } from '@heroicons/react/20/solid';
import { useLocation } from 'react-router-dom';
import Logo from './Logo';
import DesktopNavigation from './DesktopNavigation';
import MobileNavigation from './MobileNavigation';
import { MenuItem } from '../types/navigation';
import { MENU_ITEMS } from '../constants/navigation';

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState<
    string | null
  >(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const currentPath = location.pathname;

  const menuItems = MENU_ITEMS;

  const isActiveItem = (item: MenuItem): boolean => {
    if (item.href && currentPath === item.href) return true;
    if (item.subItems) {
      return item.subItems.some((subItem) => currentPath === subItem.href);
    }
    return false;
  };

  const toggleDesktopDropdown = (label: string) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) {
      setActiveMobileDropdown(null);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setActiveMobileDropdown(null);
  };

  const toggleMobileDropdown = (label: string) => {
    setActiveMobileDropdown(activeMobileDropdown === label ? null : label);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="shadow-lg sticky top-0 z-50 bg-secondary-500">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 md:h-24">
          {/* Logo */}
          <div className="flex items-center">
            <Logo />
          </div>

          {/* Desktop Navigation */}
          <DesktopNavigation
            menuItems={menuItems}
            currentPath={currentPath}
            activeDropdown={activeDropdown}
            dropdownRef={dropdownRef}
            onToggleDropdown={toggleDesktopDropdown}
            isActiveItem={isActiveItem}
          />

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-lg text-black hover:opacity-80 transition-colors duration-200 cursor-pointer"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <MobileNavigation
        menuItems={menuItems}
        currentPath={currentPath}
        isMobileMenuOpen={isMobileMenuOpen}
        activeMobileDropdown={activeMobileDropdown}
        onCloseMobileMenu={closeMobileMenu}
        onToggleMobileDropdown={toggleMobileDropdown}
        isActiveItem={isActiveItem}
      />
    </header>
  );
};

export default Header;
